import { useEffect, useState, useMemo, useCallback } from 'react';
import debounce from 'lodash/debounce';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { actions as insurancePayersActions } from '../../redux/appointmentForm/requests/insurance-payers-req';
import {
  selectInsurancePayersList,
  selectInsurancePayersLoading,
  selectInsurancePayersIsLastPage,
} from '../../redux/appointmentForm/selectors';

const LIMIT = 50;

const mapPayerToOption = payer => ({
  id: payer.id,
  value: payer.name,
  label: payer.name,
});

const useHealthPlanList = (withPlanNotListedOption = true) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const payersList = useSelector(selectInsurancePayersList);
  const isLoading = useSelector(selectInsurancePayersLoading);
  const isLastPage = useSelector(selectInsurancePayersIsLastPage);

  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!payersList.length && !isLoading) {
      dispatch(
        insurancePayersActions.send({ limit: LIMIT, offset: 0, query: '' }),
      );
    }
  }, []);

  const fetchWithSearch = useCallback(
    debounce(searchQuery => {
      dispatch(insurancePayersActions.clear());
      dispatch(
        insurancePayersActions.send({
          limit: LIMIT,
          offset: 0,
          query: searchQuery,
        }),
      );
    }, 500),
    [],
  );

  const handleInputChange = useCallback(
    (value, { action } = {}) => {
      if (action !== 'input-change') return;
      setQuery(value);
      fetchWithSearch(value);
    },
    [fetchWithSearch],
  );

  const handleMenuScrollToBottom = useCallback(() => {
    if (isLastPage || isLoading) return;

    const nextOffset = payersList.length;

    dispatch(
      insurancePayersActions.send({ limit: LIMIT, offset: nextOffset, query }),
    );
  }, [isLastPage, isLoading, payersList.length, query]);

  const planNotListedOption = useMemo(
    () => ({
      id: null,
      value: null,
      label: formatMessage({ id: 'appointments.form.planNotListed' }),
    }),
    [formatMessage],
  );

  const convertedEntities = useMemo(() => payersList.map(mapPayerToOption), [
    payersList,
  ]);

  const entities = useMemo(() => {
    return withPlanNotListedOption
      ? [planNotListedOption, ...convertedEntities]
      : convertedEntities;
  }, [withPlanNotListedOption, planNotListedOption, convertedEntities]);

  return {
    entities,
    handleInputChange,
    handleMenuScrollToBottom,
    isLoading,
  };
};

export default useHealthPlanList;



// WEBPACK FOOTER //
// ./src/hooks/selectOptions/useHealthPlanList.js